import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { Grid, Menu } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { type Nil, Text } from '@pbt/pbt-ui-components'

import OrderStateLabel from '~/components/common/labels/OrderStateLabel'
import { PrescriptionStateLabel } from '~/components/common/labels/PrescriptionStateLabel'
import WarningRowLabel from '~/components/common/labels/WarningRowLabel'
import PuiIconButton from '~/components/common/PuiIconButton'

const useStyles = makeStyles(
  (theme) => ({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.colors.tableOddRowBackground,
      },
      padding: theme.spacing(1),
      '&:not(:last-of-type)': {
        borderBottom: theme.constants.tabBorder,
      },
    },
    softText: {
      color: theme.colors.lowAccentText,
    },
    stateLabel: {
      minWidth: 55,
      maxHeight: 12,
    },
    puiIconButton: {
      backgroundColor: 'inherit',
      boxShadow: 'none',
    },
    leftInfoContainer: {
      width: 'auto',
    },
    statusContainer: {
      minWidth: 96,
      display: 'flex',
      marginRight: 'auto',
    },
  }),
  { name: 'FinalizeOrdersRow' },
)

interface OrderNameDetails {
  orderName: string | Nil
  softText?: boolean
}

interface BaseFinalizeOrdersRowProps {
  actions?: React.ReactNode
  breedWarningContent?: React.ReactNode
  labOrderId?: string | Nil
  orderNameDetails: OrderNameDetails | OrderNameDetails[] | Nil
  showWarning: boolean
  vendorName?: string | Nil
}

interface FinalizePrescription extends BaseFinalizeOrdersRowProps {
  stateId: string
  statusName?: never
}

interface FinalizeOrder extends BaseFinalizeOrdersRowProps {
  stateId?: never
  statusName: string | Nil
}

type FinalizeOrdersRowProps = FinalizePrescription | FinalizeOrder

const FinalizeOrdersRow = ({
  actions,
  breedWarningContent,
  labOrderId,
  orderNameDetails,
  showWarning,
  statusName,
  stateId,
  vendorName,
}: FinalizeOrdersRowProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Soap'])

  const ref = useRef<HTMLButtonElement>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const orderNameRender = Array.isArray(orderNameDetails) ? (
    orderNameDetails.map((details, i) => (
      <Text
        className={details.softText ? classes.softText : undefined}
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        mb={0.5}
      >
        {details.orderName}
      </Text>
    ))
  ) : (
    <Text className={orderNameDetails?.softText ? classes.softText : undefined}>
      {orderNameDetails?.orderName}
    </Text>
  )

  return (
    <Grid container className={classes.root} direction="column">
      <Grid container item xs alignItems="center">
        <Grid
          container
          item
          xs
          alignItems="center"
          className={classes.leftInfoContainer}
          datatest-id="finalize-orders-row-name"
        >
          {vendorName ? (
            <Text strong pr={1}>
              {vendorName}
              {labOrderId ? `: ${labOrderId}` : ''}
            </Text>
          ) : (
            <Grid container item>
              {orderNameRender}
            </Grid>
          )}
          {showWarning && (
            <WarningRowLabel
              message={t('Soap:MAKE_SURE_TO_COMPLETE_ORDER_WARNING_MESSAGE', {
                vendorName,
              })}
            />
          )}
        </Grid>
        <Grid
          item
          className={classes.statusContainer}
          datatest-id="finalize-orders-row-status"
          xs={2}
        >
          {statusName && (
            <OrderStateLabel
              alignSelf="center"
              className={classes.stateLabel}
              orderStatus={statusName}
              variant="small"
            >
              {statusName}
            </OrderStateLabel>
          )}
          {stateId && (
            <PrescriptionStateLabel stateId={stateId} variant="small" />
          )}
        </Grid>
        {actions && (
          <Grid
            container
            item
            datatest-id="finalize-orders-row-actions"
            justifyContent="flex-end"
            xs={1}
          >
            <PuiIconButton
              Icon={MoreHorizIcon}
              aria-controls={open ? 'finalize-orders-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              className={classes.puiIconButton}
              id="finalize-orders-menu-button"
              ref={ref}
              onClick={handleClick}
            />
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={open}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handleClose}
            >
              <Grid container flexDirection="column">
                {actions}
              </Grid>
            </Menu>
          </Grid>
        )}
      </Grid>
      {vendorName && (
        <Grid container item>
          {orderNameRender}
        </Grid>
      )}
      {breedWarningContent && (
        <Grid container item>
          {breedWarningContent}
        </Grid>
      )}
    </Grid>
  )
}

export default FinalizeOrdersRow
